import { apiClient } from '@/lib/api-client';
import { createAgGridDatasource } from '@/lib/createAgGridDatasource';
import type {
  InstrumentRunPublic,
  PaginatedSampleProteoformsResponse,
  SampleProteoformPublic,
  SampleProteoformSearchRequest,
} from '@/types';

import { type RunSampleDetailsStatus, statusFromError, statusFromResponse } from './runSampleDetailsStatus';

export interface RunSampleDetailsStats {
  numProteoformGroups: number;
  numPtms: number;
  numIsoforms: number;
  /** Rows matching the current filters, so Download can tell an empty result from one with rows. */
  total: number;
  numWithheldQcFailure: number;
  numWithheldQcPending: number;
}

interface RunSampleDetailsDatasourceOptions {
  url: string;
  thresholdCpm: number;
  validSortFields: readonly string[];
  epitopeKeys: string[];
  decodeStatus: InstrumentRunPublic['decode_status'];
  onStats: (stats: RunSampleDetailsStats | null) => void;
  onStatus: (status: RunSampleDetailsStatus) => void;
}

export function createRunSampleDetailsDatasource({
  url,
  thresholdCpm,
  validSortFields,
  epitopeKeys,
  decodeStatus,
  onStats,
  onStatus,
}: RunSampleDetailsDatasourceOptions) {
  // Inactive until installed and once replaced, so a superseded datasource's late outcomes are dropped.
  let isActive = false;
  // Only the newest-issued request may set status, so a slow superseded outcome can't overwrite it.
  // Keyed by grid request too, so a sort fallback's body keeps its original request's place.
  const requestIds = new WeakMap<object, number>();
  let latestRequest = 0;
  const isLatest = (body: SampleProteoformSearchRequest) => isActive && requestIds.get(body) === latestRequest;

  const datasource = createAgGridDatasource<
    SampleProteoformPublic,
    PaginatedSampleProteoformsResponse,
    SampleProteoformSearchRequest
  >(
    url,
    ({ limit, skip, sortBy, sortOrder, filters, request }) => {
      // A cleared or unknown sort would send the default `created_at`, which this endpoint 422s.
      // `sample_name` asc matches the grid's initial sort: validSortFields[0], the first PROTEOFORM_SORT_FIELDS entry.
      const isKnownSort = validSortFields.includes(sortBy);
      const body: SampleProteoformSearchRequest = {
        skip,
        limit,
        sort_by: isKnownSort ? sortBy : 'sample_name',
        sort_order: isKnownSort ? sortOrder : 'asc',
        threshold_cpm: thresholdCpm,
        filters: Object.keys(filters).length > 0 ? (filters as SampleProteoformSearchRequest['filters']) : undefined,
      };
      let id = requestIds.get(request);
      if (id === undefined) {
        latestRequest += 1;
        id = latestRequest;
        requestIds.set(request, id);
      }
      requestIds.set(body, id);
      return body;
    },
    {
      booleanFields: epitopeKeys,
      // Always refetch: a cached pre-decode empty page would otherwise read as "no results" once decoded.
      staleTime: 0,
      fetcher: async body => {
        const response = await apiClient.post<PaginatedSampleProteoformsResponse>(url, body);
        // An empty body would skip onResponse and leave the previous status (e.g. a stale 403) on screen.
        if (response === null) throw new Error('Empty response from run proteoform search');
        return response;
      },
      onResponse: (response, body) => {
        if (!isLatest(body)) return;
        // Counts are server-computed at this threshold and ignore filters; `total` is the filtered row count.
        onStats({
          numProteoformGroups: response.num_proteoform_groups,
          numPtms: response.num_ptms,
          numIsoforms: response.num_isoforms,
          total: response.total,
          numWithheldQcFailure: response.num_samples_withheld_qc_failure,
          numWithheldQcPending: response.num_samples_withheld_qc_pending,
        });
        onStatus(statusFromResponse(response, body.filters !== undefined, decodeStatus));
      },
      onTotalError: (error, body) => {
        // No body means building it threw: report it while installed, and outrank older in-flight requests.
        if (body === undefined ? !isActive : !isLatest(body)) return;
        if (body === undefined) latestRequest += 1;
        onStats(null);
        onStatus(statusFromError(error));
      },
    },
  );
  return Object.assign(datasource, {
    setActive: (active: boolean) => {
      isActive = active;
    },
  });
}
